import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { LinearProgress } from '@mui/material'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import TenantService from '@/modules/_services/tenantService'
import { fetchData, switchTenant } from '@/redux/ducks/user/info'
import { closeTenantSelection, showLoader, hideLoader } from '@/redux/ducks/tenant/selection'
import { toast } from '@/modules/base/components/toast/toastConfig'
import ThemeInterface from '@/theme-interface'
import TenantSelectionItemBase from '@/modules/base/components/tenant-selection/items/tenant-selection-item-base'
import { ConfirmModal } from '@/modules/base/components/modal'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconPlus from '@assets/images/icons/plus.svg'
import { useAppDispatch } from '@/redux/store'
import { navigate } from '@/modules/layout/util/useInterceptedNavigate'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  content: {
    '&:hover': {
      color: theme.primaryBright,
    },
  },
  action: {
    fontSize: theme.fontSizes.smaller,
    color: theme.primary,
    cursor: 'pointer',
  },
  loaderWrapper: {
    width: 60,
  },
  loaderProgress: {
    backgroundColor: theme.primaryBright,
  },
  icon: {
    fill: theme.primary,
  },
}), { name: 'TenantSelectionCreateItem' })
interface TenantSelectionCreateProps {
  isDemo?: boolean,
  createDemo?: boolean,
  hideSeperator?: boolean,
  onCreated?: (tenantId: string) => void
}

const TenantSelectionCreateItem = (props: TenantSelectionCreateProps) => {
  const dispatch = useAppDispatch()
  const classes = useStyles()
  const { isDemo, createDemo, hideSeperator, onCreated } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false)
  const { hasDemo, isOwner } = useSelector(state => state.user.info)
  const userEmail = useSelector(state => state.user.info.email)
  const subscriptions = useSelector(state => state.subscription.details.subscriptions)
  const { isTaxAccountant, allowAdditionalTenants } = useMemo(() => {
    return subscriptions.reduce((result, subscription) => {
      if (subscription.isTaxAccountant) {
        result.isTaxAccountant = true
      }
      if (subscription.allowAdditionalTenants) {
        result.allowAdditionalTenants = true
      }

      return result
    }, { isTaxAccountant: false, allowAdditionalTenants: false })
  }, [subscriptions])
  const canCreateTenant = (!isTaxAccountant && allowAdditionalTenants)
  const canCreateDemoTenant = (!hasDemo && isOwner && !isTaxAccountant)

  // return null if user has no rights to create tenant or demo tenant
  if ((!createDemo && !canCreateTenant) || (createDemo && !canCreateDemoTenant)) { return null }

  const selectTenantDone = (error, tenant): void => {
    if (!error) {
      const getDisplayName = ({ tenantName, companyName }) => {
        return tenantName || companyName || (isTaxAccountant ? lang.tenant.newOffice : lang.tenant.newTenant)
      }
      toast.showToast({ type: 'info', message: lang.tenant.tenantActivated(getDisplayName(tenant)) })
    } else {
      let errorMessage = lang.defaultErrorMessage
      if (error?.body?.meta?.switch) {
        errorMessage = lang.tenant.switchNotAllowed
      }

      toast.showToast({ type: 'error', message: errorMessage })
    }

    dispatch(hideLoader())
  }

  const createTenant = async(): Promise<void> => {
    if (isLoading) { return }
    setIsLoading(true)
    try {
      const tenantId = await TenantService.createTenant(createDemo)
      dispatch(fetchData())
      if (onCreated) { onCreated(tenantId) }
      dispatch(showLoader())
      dispatch(closeTenantSelection())
      dispatch(switchTenant({ tenantId }, selectTenantDone))
      navigate('/firstSteps', { replace: true })
      // error is handled by the tenant service
    } finally {
      setIsLoading(false)
    }
  }

  const action = isLoading
    ? (
      <div className={classes.loaderWrapper}>
        <LinearProgress variant="indeterminate" classes={{
          barColorPrimary: classes.loaderProgress,
        }} />
      </div>
    )
    : null

  return (
    <>
      <TenantSelectionItemBase
        hideSeperator={hideSeperator || !canCreateDemoTenant}
        tenantName={createDemo ? lang.tenant.demoTenant : lang.tenant.createNewTenant }
        styles={{ content: classes.content }}
        action={action}
        customIcon={<SVGIcon svg={IconPlus} width={12} height={12} className={classes.icon} />}
        onClick={() => setIsConfirmOpen(true)} />
      {isConfirmOpen && (
        <ConfirmModal
          open={isConfirmOpen}
          title= {isDemo ? lang.tenant.switchConfirmation.titleDemoTenant : lang.tenant.switchConfirmation.titleNewCompany}
          description={<div dangerouslySetInnerHTML={{ __html: lang.tenant.switchConfirmation.description(userEmail) }} />}
          onConfirm={() => { setIsConfirmOpen(false); createTenant() }}
          onAbort={() => setIsConfirmOpen(false)}
          confirmButtonLabel= {lang.tenant.switchConfirmation.confirmButtonLabel}
          dataQsId={isDemo ? 'confirmCreateDemoTenant' : 'confirmCreateTenant'}
        />
      )}    </>
  )
}

export default TenantSelectionCreateItem
